import classNames from "classnames"
import type { RefCallback } from "react"

import { useBgColorContext } from "./context"

import LearnMoreButton from "shared/components/learn_more_button"
import ReturnButton from "./return_button"
import HeaderIconButton from "./header_icon_button"
import UpgradeBanner from "./upgrade_banner"
import OutOfCreditsBanner from "./out_of_credits_banner"
import { steps } from "./steps"
import colors from "foundation/_colors_export.scss?export"
import { canUpgradeCredits, compareTier } from "shared/utils/tiers"
import { CreditsBalance } from "shared/utils/credits"
import { Tier } from "./utils/interfaces"

import iconChevronLeft from "images/icons/chevron-left.svg?raw"

import styles from "./header.module.scss"

interface HeaderProps {
  editing: boolean
  backButtonTarget: () => void
  title: string
  user: {
    tier: Tier
    eligibleForTrial: boolean
    eligibleTrialPeriod?: number
    permissions: {
      [key: string]: {
        permitted: boolean
      }
    }
  }
  userAtQuota: boolean
  step: number
  showingCheckoutModal: boolean
  showUpgradeModal: (step: string, tier: Tier | undefined) => void
  appletTier: Tier
  chatStyle?: boolean
  // A step's own action renders into this slot; it replaces the help button.
  actionSlotRef?: RefCallback<HTMLDivElement>
  compact?: boolean
  creditsBalance?: CreditsBalance | null
}

export const Header = ({
  editing,
  backButtonTarget,
  title,
  user,
  userAtQuota,
  step,
  showingCheckoutModal,
  showUpgradeModal,
  appletTier,
  chatStyle = false,
  actionSlotRef,
  compact = false,
  creditsBalance,
}: HeaderProps) => {
  // No loading state here: the leave prompt can be cancelled, and swapping
  // the canvas for a spinner remounts its images into the very window where
  // the browser aborts the cancelled navigation's requests — they come back
  // broken. Whoever does leave gets the browser's own loading feedback.
  const onReturn = () => {
    if (initialStep) {
      window.App.Scripts.BackButton('/explore')
    } else {
      backButtonTarget()
    }
  }

  const bgColor = useBgColorContext()
  const bgOnColor = bgColor !== "white"
  const color = bgOnColor ? "white" : colors.primaryTextColor
  const classes = classNames(styles["diy-header"], { [styles.compact]: compact })

  const initialStep = step === steps.INITIAL
  const allowedScreenForBanner = initialStep && !showingCheckoutModal
  const outOfCredits = allowedScreenForBanner && creditsBalance?.status === "empty"
  // No-op until the credits checkout modal is implemented (https://app.shortcut.com/ifttt/story/146926/web-plan-upgrade-checkout-credit-model).
  const creditsUpgradeHandler = canUpgradeCredits(user.tier) ? () => {} : undefined

  let bannerClickHandler = () => {}
  let ctaReason = ""

  if (allowedScreenForBanner) {
    if (compareTier(appletTier, user.tier) > 0) {
      ctaReason = appletTier === "pro" ? "default_pro" : "default_intermediate_pro"
      bannerClickHandler = () => showUpgradeModal(ctaReason, appletTier)
    } else if (userAtQuota && !editing) {
      ctaReason = "applet_quota"
      bannerClickHandler = () => showUpgradeModal(ctaReason, user.tier === "free" ? "intermediate_pro" : "pro")
    }
  }

  return (
    <>
      <header style={{ backgroundColor: bgColor }} className={classes}>
        {chatStyle ? (
          <HeaderIconButton icon={iconChevronLeft} title="Cancel" onClick={onReturn} />
        ) : (
          <ReturnButton onClick={onReturn} initialStep={initialStep} color={color} />
        )}
        {title && <h1 style={{ color }}>{title}</h1>}
        {actionSlotRef ? (
          <div className={styles.action} ref={actionSlotRef} />
        ) : !user.permissions?.zendesk_support?.permitted ? (
          <LearnMoreButton
            url="https://ift.tt/applets-101"
            darkHeader={bgOnColor}
            title="Learn more: Best Practices - Applets 101"
          />
        ) : (
          // zendesk-permitted users keep the floating widget and its open/close
          // choreography — an empty placeholder so justify-between still holds.
          <div />
        )}
      </header>
      {outOfCredits && <OutOfCreditsBanner balance={creditsBalance} onUpgrade={creditsUpgradeHandler} />}
      {allowedScreenForBanner && ctaReason && (
        <UpgradeBanner ctaReason={ctaReason} eligibleForTrial={user.eligibleForTrial} eligibleTrialPeriod={user.eligibleTrialPeriod} onClick={bannerClickHandler} appletTier={appletTier} />
      )}
    </>
  )
}

export default Header
